import React from "react";
import { DeepMap, FieldError } from "react-hook-form";
import styles from "./forms.module.css";
import { StringIndexedDict } from "../../api/GenericTypes";
import { ErrorsRenderer } from "./MultiEditForms/common/MultiEditRenderUtils";
import { Button } from "../buttons/Button/Button";
import { LucideIcon } from "../icon/LucideIcon";

interface FormButtonsProps {
  onClose?: () => void;
  onSubmit?: () => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  submitButtonLabel?: string;
  errors?: DeepMap<any, FieldError>;
  children?: React.ReactNode;
  hideCancel?: boolean;
  btnCls?: string;
  reset?: () => void;
  dirtyFields?: number;
}

/**
 * Renders a set of form action buttons including Submit, Cancel, and optionally a Revert button.
 * Also displays form errors and allows for custom child elements.
 * @author @CorradoSurmanowicz
 * @param onClose - Optional callback invoked when the Cancel button is clicked.
 * @param onSubmit - Callback invoked when the Submit button is clicked.
 * @param loading - If true, shows loading indicators on buttons.
 * @param disabled - If true, disables all action buttons.
 * @param submitButtonLabel - Label for the Submit button. Defaults to "Save changes".
 * @param errors - Object containing form errors to display below the buttons.
 * @param children - Optional React nodes to render before the action buttons.
 * @param hideCancel - If true, hides the Cancel button. Defaults to false.
 * @param btnCls - Optional CSS class for styling the buttons.
 * @param reset - Optional callback invoked when the Revert button is clicked. If provided, shows the Revert button.
 * @param dirtyFields - Optional indicator (e.g., count or label) of dirty fields, shown as a badge on the Submit button.
 */
export const FormButtons = ({
  onClose,
  onSubmit,
  loading,
  disabled,
  submitButtonLabel = "Save changes",
  errors,
  children,
  hideCancel = false,
  btnCls,
  reset,
  dirtyFields,
}: FormButtonsProps) => {
  return (
    <>
      <div className={styles.header}>
        <div className={styles.buttons}>
          {children}
          {!hideCancel && (
            <Button
              className={"btn btn-ghost-secondary"}
              type="button"
              onClick={() => {
                onClose?.();
              }}
              disabled={disabled || loading}
              title="Cancel"
            >
              <LucideIcon name="x" /> Cancel
            </Button>
          )}
          {reset && (
            <Button
              className={btnCls ?? "btn btn-ghost-secondary"}
              type="button"
              onClick={reset}
              disabled={disabled || loading}
              title="Revert"
            >
              <LucideIcon name="refresh-ccw" /> Revert
            </Button>
          )}
          <div style={{ width: "1px", height: "37px", borderLeft: "1px solid var(--gray-200)" }} />
          <Button
            className={btnCls ?? "btn btn-primary"}
            type="button"
            onClick={async () => await onSubmit?.()}
            disabled={disabled}
            loading={loading}
            title={submitButtonLabel}
          >
            {submitButtonLabel} {!!dirtyFields && <span className="badge">{dirtyFields}</span>}
          </Button>
        </div>
      </div>

      {typeof errors === "object" && !!Object.keys(errors).length && (
        <div style={{ marginTop: 10, padding: 0 }}>
          <FormErrors errors={errors} />
        </div>
      )}
    </>
  );
};

interface Errors {
  [fieldId: string]: FieldError;
}

interface FormErrorProps<Form extends StringIndexedDict> {
  errors: Errors | DeepMap<Form, FieldError>;
  groupName?: string; // DEPRECATE
}
/**
 * Renders a list of form validation errors.
 * @author @CorradoSurmanowicz
 * @template Form - The type representing the form's fields as a string-indexed dictionary.
 * @param errors - An object containing error messages for each form field.
 * @returns A React element displaying the list of errors if any exist, otherwise an empty fragment.
 */
export const FormErrors = <Form extends StringIndexedDict>({ errors }: FormErrorProps<Form>) => {
  if (Object.keys(errors).length > 0) {
    return (
      <div className="flex" style={{ width: "100%" }}>
        <ul className={styles.errorList}>
          {Object.entries(errors).map(([path, error], index) => {
            return <ErrorsRenderer error={error} key={index} path={path} />;
            // return <React.Fragment key={index}>{error.message && <li>{error.message}</li>}</React.Fragment>;
          })}
        </ul>
      </div>
    );
  } else return <></>;
};

export const flattenErrors = (value: any, index: any): any => {
  if (value.message) return <li>{value.message}</li>;
  else return Object.values(value).flatMap(flattenErrors);
};
